"use client";

import React from "react";
import { Button, Checkbox } from "@west-tennessee-consulting/ui";
import { Loader2, ChevronLeft, ChevronRight, AlertCircle } from "lucide-react";
import type { ApolloContact } from "@/lib/apollo/types";

interface ContactSelectionStepProps {
  companyName: string;
  contacts: ApolloContact[];
  selectedContacts: Set<string>;
  onToggleContact: (contactId: string) => void;
  onToggleSelectAll: () => void;
  onImport: () => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
}

export function ContactSelectionStep({
  companyName,
  contacts,
  selectedContacts,
  onToggleContact,
  onToggleSelectAll,
  onImport,
  onBack,
  loading,
  error,
}: ContactSelectionStepProps) {
  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-medium mb-2">Decision Makers at {companyName}</div>
        <div className="text-xs text-gray-600 dark:text-gray-400">
          {selectedContacts.size} of {contacts.length} selected
        </div>
      </div>

      <div className="flex gap-2 mb-2">
        <Button variant="outline" size="sm" onClick={onToggleSelectAll}>
          {selectedContacts.size === contacts.length ? "Deselect All" : "Select All"}
        </Button>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto border rounded p-2">
        {contacts.length === 0 ? (
          <div className="text-sm text-gray-500 p-4 text-center">
            No decision makers found at this company
          </div>
        ) : (
          contacts.map((contact) => (
            <label
              key={contact.id}
              className="flex items-start gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer"
            >
              <Checkbox
                checked={selectedContacts.has(contact.id)}
                onCheckedChange={() => onToggleContact(contact.id)}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">
                  {contact.first_name} {contact.last_name}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{contact.job_title ?? ""}</div>
                <div className="text-xs text-gray-500">{contact.email ?? ""}</div>
                {contact.phone_number && (
                  <div className="text-xs text-gray-500">{contact.phone_number}</div>
                )}
              </div>
            </label>
          ))
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack} disabled={loading} className="flex-1">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={onImport}
          disabled={loading || selectedContacts.size === 0}
          className="flex-1"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Importing...
            </>
          ) : (
            <>
              Import ({selectedContacts.size})
              <ChevronRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
